/**
 * Phase 46 (Plan 46-07) — TanStack hooks for per-project notification routes.
 *
 * Consumed by Plan 46-08 ProjectNotificationsTab on ProjectDetailPage. Hook
 * key shape `['notificationRoutes', projectId]` is mirrored by the mutation
 * invalidators below.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProjectNotificationRoutes,
  createProjectNotificationRoute,
  updateProjectNotificationRoute,
  deleteProjectNotificationRoute,
} from '../services/api/notificationRoutes';
import type {
  CreateProjectNotificationRouteInput,
  UpdateProjectNotificationRouteInput,
} from '../services/api/notificationRoutes';

export function useNotificationRoutes(projectId: number | undefined) {
  return useQuery({
    queryKey: ['notificationRoutes', projectId],
    queryFn: () => fetchProjectNotificationRoutes(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateNotificationRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      input,
    }: {
      projectId: number;
      input: CreateProjectNotificationRouteInput;
    }) => createProjectNotificationRoute(projectId, input),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['notificationRoutes', projectId] });
    },
  });
}

export function useUpdateNotificationRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      routeId,
      input,
    }: {
      projectId: number;
      routeId: number;
      input: UpdateProjectNotificationRouteInput;
    }) => updateProjectNotificationRoute(projectId, routeId, input),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['notificationRoutes', projectId] });
    },
  });
}

export function useDeleteNotificationRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, routeId }: { projectId: number; routeId: number }) =>
      deleteProjectNotificationRoute(projectId, routeId),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['notificationRoutes', projectId] });
    },
  });
}
